import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router";
import { ItemChangedReason } from "dnd-kit-sortable-tree/dist/types";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useProject } from "context/ProjectContext";
import useStructuredTemplate from "hooks/api/REST/structuredTemplates/useStructuredTemplate";
import useUpdateStructuredTemplate from "hooks/api/REST/structuredTemplates/useUpdateStructuredTemplate";
import usePermissions from "hooks/permissions";
import { IStructuredTemplateSection } from "models/structuredTemplates.models";
import {
  DRAG_AND_DROP_DEBOUNCE_WAIT_MS,
  StructuredTemplateAction,
} from "../../screens/Project/screens/StructuredDocument/StructuredDocument.constants";
import {
  TStructuredTemplateContextValue,
  TStructuredTemplateTreeItem,
} from "./StructuredTemplateContext.types";
import {
  getSectionsFromTreeItems,
  getTreeItemsFromSections,
  getTreeViewItemsByPath,
  recalculateTreeItemPaths,
  recursivelyCollapse,
} from "./StructuredTemplateContext.utils";

export const StructuredTemplateContext = createContext<
  TStructuredTemplateContextValue | undefined
>(undefined);

const StructuredTemplateProvider = () => {
  const { t } = useTranslation("common", { keyPrefix: "notifications" });

  const { project } = useProject();
  const { permissions } = usePermissions(project?.id ?? "");
  const { structuredTemplateId } = useParams();

  const { structuredTemplate, isFetching, isError } = useStructuredTemplate(
    structuredTemplateId ?? "",
    project?.id ?? "",
    { enabled: !!structuredTemplateId && !!project?.id },
  );

  const { updateStructuredTemplateAsync } = useUpdateStructuredTemplate({
    successMessage: t("structuredTemplateOrderUpdateSuccess"),
    failureMessage: t("structuredTemplateOrderUpdateFailure"),
  });

  const [treeViewItems, setTreeViewItems] = useState<
    TStructuredTemplateTreeItem[]
  >([]);

  // This keeps the value current for the effect below to use without changes causing the effect to
  // re-run. We only want to re-initialize the items from the template when the template itself
  // changes.
  const treeViewItemsRef = useRef(treeViewItems);
  treeViewItemsRef.current = treeViewItems;

  useEffect(() => {
    const treeViewItemsByPath = getTreeViewItemsByPath(
      treeViewItemsRef.current,
    );

    return setTreeViewItems(
      getTreeItemsFromSections(
        structuredTemplate?.sections ?? [],
        treeViewItemsByPath,
      ),
    );
  }, [structuredTemplate]);

  // True starting when user starts dragging through debouncing and ending when API call is finished
  const [isSortingInProgress, setIsSortingInProgress] = useState(false);

  const debouncedSaveStructuredTemplate = useMemo(
    () =>
      debounce(async (updatedSections: IStructuredTemplateSection[]) => {
        if (!structuredTemplate || !project) {
          return;
        }

        await updateStructuredTemplateAsync({
          templateId: structuredTemplate.id,
          projectId: project.id,
          data: {
            ...structuredTemplate,
            sections: updatedSections,
          },
        });

        setIsSortingInProgress(false);
      }, DRAG_AND_DROP_DEBOUNCE_WAIT_MS),
    [project, structuredTemplate, updateStructuredTemplateAsync],
  );

  const handleTreeViewItemsChanged = useCallback(
    (
      updatedTreeViewItems: TStructuredTemplateTreeItem[],
      reason?: ItemChangedReason<TStructuredTemplateTreeItem>,
    ) => {
      const dropParentPath =
        reason?.type === "dropped" && reason.droppedToParent
          ? reason.droppedToParent.id.toString()
          : null;

      const recalculatedTreeViewItems =
        recalculateTreeItemPaths(updatedTreeViewItems);

      setTreeViewItems(
        recursivelyCollapse(recalculatedTreeViewItems, dropParentPath),
      );

      const updatedSections = getSectionsFromTreeItems(
        recalculatedTreeViewItems,
      );

      const isDeepEqual = isEqual(
        updatedSections,
        structuredTemplate?.sections ?? [],
      );

      if (!isDeepEqual) {
        setIsSortingInProgress(true);
        debouncedSaveStructuredTemplate(updatedSections);
      }
    },
    [structuredTemplate, debouncedSaveStructuredTemplate],
  );

  const canManageStructuredTemplate =
    permissions.canManageStructuredTemplate && !!project?.active;

  const structuredTemplateActions = useMemo(() => {
    if (canManageStructuredTemplate) {
      return new Set([
        StructuredTemplateAction.AddSection,
        StructuredTemplateAction.EditSectionTitle,
        StructuredTemplateAction.DeleteSection,
        StructuredTemplateAction.AddSubsection,
      ]);
    } else {
      return new Set<StructuredTemplateAction>();
    }
  }, [canManageStructuredTemplate]);

  const value = useMemo(
    () => ({
      structuredTemplate,
      treeViewItems,
      handleTreeViewItemsChanged,
      isStructuredTemplateBusy: isFetching || isSortingInProgress,
      structuredTemplateActions,
      isErrorLoadingStructuredTemplate: isError,
      canManageStructuredTemplate,
    }),
    [
      structuredTemplate,
      treeViewItems,
      handleTreeViewItemsChanged,
      isFetching,
      isSortingInProgress,
      structuredTemplateActions,
      isError,
      canManageStructuredTemplate,
    ],
  );

  return (
    <StructuredTemplateContext.Provider value={value}>
      <Outlet />
    </StructuredTemplateContext.Provider>
  );
};

export default StructuredTemplateProvider;
